/**
 * Linear Assets — Flight Data & AI Corridor Diagnostics (Step 6)
 *
 * Upload corridor stereo RGB and LiDAR point clouds via real chunked upload.
 * Two-step flow: files upload independently → "Finalise Upload" button → proceed.
 */

import React, { useState } from 'react';
import { Microscope, Layers, CheckCircle2, ArrowRight, Upload } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import ChunkedUpload from '../../components/common/ChunkedUpload';
import { useNavigate } from 'react-router-dom';

export const LinearFlightAnalysisPage: React.FC = () => {
  const navigate = useNavigate();

  // Mission context — in a real app this would come from route params / store
  const missionId    = 'linear-sector7-001';
  const missionLabel = 'Power Line Sector 7 — Km 12.4–14.1';

  // Upload state
  const [uploadedSessionIds, setUploadedSessionIds] = useState<string[]>([]);
  const [finalising,          setFinalising]          = useState(false);
  const [finalised,           setFinalised]           = useState(false);
  const [error,               setError]               = useState<string | null>(null);
  const [success,             setSuccess]             = useState<string | null>(null);

  const handleFileUploaded = (sessionId: string, _filePath: string) => {
    setUploadedSessionIds((prev) => [...prev, sessionId]);
  };

  const handleFinaliseUpload = async () => {
    if (uploadedSessionIds.length === 0) return;
    setFinalising(true);
    setError(null);
    try {
      const api = (await import('../../services/api')).default;
      await api.patch(`/missions/${missionId}`, {
        metadata: {
          upload_session_ids: uploadedSessionIds,
          upload_completed_at: new Date().toISOString(),
          vertical: 'linear_assets',
          step: 6,
        },
      });
      setFinalised(true);
      setSuccess(`${uploadedSessionIds.length} file session${uploadedSessionIds.length > 1 ? 's' : ''} confirmed for mission "${missionLabel}"`);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to confirm upload');
    } finally {
      setFinalising(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Microscope className="w-7 h-7 text-amber-500" /> Linear Flight Data &amp; AI Analysis (Step 6)
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Ingest corridor stereo RGB and LiDAR point clouds — AI canopy height and conductor proximity extraction
          </p>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Mission context card ── */}
      <Card>
        <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800 mb-4">
          <div>
            <h3 className="font-bold text-base text-gray-900 dark:text-white">Survey Flight — {missionLabel}</h3>
            <p className="text-xs text-gray-500">MicaSense RedEdge + Zenmuse L2 LiDAR · Mission ID: {missionId}</p>
          </div>
          <span className={`px-2.5 py-0.5 rounded text-xs font-bold uppercase ${finalised ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {finalised ? 'Upload Complete' : 'Awaiting Data Upload'}
          </span>
        </div>

        {/* ── Accepted file types info ── */}
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 mb-4">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Accepted file types
          </p>
          <div className="flex flex-wrap gap-1.5">
            {['.tif/.tiff', '.jpg/.jpeg', '.las/.laz', '.csv', '.kml', '.gpx', '.pdf'].map((t) => (
              <span key={t} className="px-2 py-0.5 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-xs font-mono text-gray-700 dark:text-gray-300">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* ── Chunked upload ── */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Upload className="w-4 h-4 text-amber-500" /> Upload Corridor Data Files
          </p>
          <ChunkedUpload
            accept=".tif,.tiff,.jpg,.jpeg,.las,.laz,.csv,.kml,.gpx,.pdf"
            onComplete={handleFileUploaded}
          />
        </div>

        {/* ── Finalise button (appears once at least one file uploaded) ── */}
        {uploadedSessionIds.length > 0 && !finalised && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
            <Button
              variant="primary"
              onClick={handleFinaliseUpload}
              loading={finalising}
              className="flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirm Upload Complete ({uploadedSessionIds.length} file{uploadedSessionIds.length > 1 ? 's' : ''})
            </Button>
            <p className="text-xs text-gray-400">Click to record the upload sessions against this mission before proceeding</p>
          </div>
        )}

        {/* ── AI Diagnosis (shown after upload finalised) ── */}
        {finalised && (
          <div className="mt-5 space-y-4">
            <div className="p-4 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-xs space-y-2">
              <p className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                AI Corridor Clearance Diagnosis:
              </p>
              <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                Stereo photogrammetry and point-cloud segmentation confirm 4 Eucalyptus trees at Km 13.2 have breached the 5.0m radial safety envelope of phase conductor B. Radial distance to conductor: <strong>3.82 metres</strong>. Flashover risk under high summer thermal loading.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              {[
                { label: 'Canopy Height Max',  value: '16.4 m',         cls: 'text-gray-900 dark:text-white' },
                { label: 'Radial Clearance',   value: '3.82 m (BREACH)', cls: 'text-red-600 dark:text-red-400' },
                { label: 'Target Span Length', value: '140 m',          cls: 'text-gray-900 dark:text-white' },
                { label: 'AI Confidence',      value: '96.4%',          cls: 'text-green-600 dark:text-green-400' },
              ].map(({ label, value, cls }) => (
                <div key={label} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-400 block">{label}</span>
                  <span className={`font-bold ${cls}`}>{value}</span>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <Button variant="primary" onClick={() => navigate('/linear-assets/analysis-review')} className="flex items-center gap-1.5">
                Proceed to Step 7 — Analysis Review <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LinearFlightAnalysisPage;
